<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  useId,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from '../../../../components/Modal.vue';
import Button from '../../../../components-next/button/Button.vue';
import {
  dismissMenuOnFocusOut,
  releaseMenuFocusBudget,
} from '../helpers/menuDismissal';

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();
const isDropdownOpen = ref(false);
const showModal = ref(false);
const isMobile = ref(window.innerWidth < 768);
const selectorRef = ref(null);
const triggerRef = ref(null);
const optionRefs = ref([]);
const activeOptionIndex = ref(0);
const dropdownId = `pipeline-selector-options-${useId()}`;
const selectorLoading = ref(false);
let accountRequestId = 0;

const pipelines = computed(() => store.getters['pipelines/getPipelines']);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const uiFlags = computed(() => store.getters['pipelines/getUIFlags']);
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const hasActiveAccount = computed(
  () => String(currentAccountId.value ?? '').trim() !== ''
);
const isLoadingPipelines = computed(
  () => selectorLoading.value || uiFlags.value.isFetching
);
const visiblePipelines = computed(() =>
  isLoadingPipelines.value || !hasActiveAccount.value ? [] : pipelines.value
);
const visibleSelectedPipeline = computed(() =>
  isLoadingPipelines.value || !hasActiveAccount.value
    ? null
    : selectedPipeline.value
);

const focusOption = index => {
  const options = optionRefs.value.filter(Boolean);
  if (!options.length) return;
  const nextIndex = (index + options.length) % options.length;
  activeOptionIndex.value = nextIndex;
  options[nextIndex]?.focus();
};

const openDesktopDropdown = async () => {
  // Este container não é recriado a cada abertura, então a permissão de
  // recuperação de foco precisa ser devolvida explicitamente — senão uma
  // sessão anterior que a consumiu faria esta fechar na primeira perda de foco.
  releaseMenuFocusBudget(selectorRef.value);
  isDropdownOpen.value = true;
  await nextTick();
  const selectedIndex = visiblePipelines.value.findIndex(
    pipeline => pipeline.id === visibleSelectedPipeline.value?.id
  );
  activeOptionIndex.value = selectedIndex >= 0 ? selectedIndex : 0;
  focusOption(activeOptionIndex.value);
};

const toggleDropdown = async () => {
  if (isLoadingPipelines.value) return;
  if (isMobile.value) {
    showModal.value = true;
  } else if (isDropdownOpen.value) {
    isDropdownOpen.value = false;
  } else {
    await openDesktopDropdown();
  }
};

const selectPipeline = async pipeline => {
  if (isLoadingPipelines.value) return;
  try {
    if (selectedPipeline.value?.id !== pipeline.id) {
      await store.dispatch('pipelines/setSelectedPipeline', pipeline);
    }
    isDropdownOpen.value = false;
    showModal.value = false;
    await nextTick();
    triggerRef.value?.$el?.focus?.();
  } catch (error) {
    // Notifica o usuário sobre o erro
    showAlert(t('PIPELINE.ERRORS.PIPELINE_SELECTION_FAILED'));
  }
};

// Atualiza o estado mobile quando a janela é redimensionada
const handleResize = () => {
  isMobile.value = window.innerWidth < 768;
  isDropdownOpen.value = false;
};

const closeDropdown = event => {
  if (!selectorRef.value?.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

const handleFocusout = event => {
  if (!isDropdownOpen.value) return;
  dismissMenuOnFocusOut(event, () => {
    isDropdownOpen.value = false;
  });
};

const handleKeydown = event => {
  if (event.key === 'Escape' && (isDropdownOpen.value || showModal.value)) {
    event.preventDefault();
    isDropdownOpen.value = false;
    showModal.value = false;
    nextTick(() => triggerRef.value?.$el?.focus?.());
    return;
  }

  if (!isDropdownOpen.value || isMobile.value) return;
  const options = optionRefs.value.filter(Boolean);
  const currentIndex = options.indexOf(document.activeElement);
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    focusOption(currentIndex + 1);
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    focusOption(currentIndex - 1);
  } else if (event.key === 'Home') {
    event.preventDefault();
    focusOption(0);
  } else if (event.key === 'End') {
    event.preventDefault();
    focusOption(options.length - 1);
  }
};

const loadPipelinesForAccount = async ({ reset = false } = {}) => {
  accountRequestId += 1;
  const requestId = accountRequestId;
  const accountId = String(currentAccountId.value ?? '').trim();
  const requestIsCurrent = () =>
    requestId === accountRequestId &&
    accountId !== '' &&
    accountId === String(currentAccountId.value ?? '').trim();

  isDropdownOpen.value = false;
  showModal.value = false;
  optionRefs.value = [];
  selectorLoading.value = true;

  try {
    if (reset) await store.dispatch('pipelines/reset');
    if (!accountId) return;
    await store.dispatch('pipelines/fetch', { accountId });

    if (!requestIsCurrent()) return;

    // Se não houver funil selecionado e existirem funis, seleciona o primeiro
    if (!selectedPipeline.value && pipelines.value.length > 0) {
      await store.dispatch('pipelines/setSelectedPipeline', pipelines.value[0]);
    }
  } catch (error) {
    if (!requestIsCurrent()) return;
    // Notifica o usuário sobre o erro
    showAlert(t('PIPELINE.ERRORS.PIPELINE_FETCH_FAILED'));
  } finally {
    if (requestId === accountRequestId) selectorLoading.value = false;
  }
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    const accountChanged =
      previousAccountId !== undefined &&
      String(accountId ?? '') !== String(previousAccountId ?? '');
    loadPipelinesForAccount({ reset: accountChanged });
  },
  { immediate: true, flush: 'sync' }
);

onMounted(() => {
  window.addEventListener('resize', handleResize);
  document.addEventListener('click', closeDropdown);
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  accountRequestId += 1;
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('click', closeDropdown);
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <div
    ref="selectorRef"
    class="pipeline-selector relative min-w-0"
    @focusout="handleFocusout"
  >
    <Button
      ref="triggerRef"
      type="button"
      variant="outline"
      color="slate"
      size="md"
      :aria-haspopup="isMobile ? 'dialog' : 'menu'"
      :aria-expanded="isDropdownOpen || showModal"
      :aria-controls="!isMobile && isDropdownOpen ? dropdownId : undefined"
      :aria-label="
        visibleSelectedPipeline
          ? `${t('PIPELINE.SELECT_PIPELINE')}: ${visibleSelectedPipeline.name}`
          : t('PIPELINE.SELECT_PIPELINE')
      "
      class="max-w-full"
      :is-loading="isLoadingPipelines"
      :disabled="isLoadingPipelines"
      @click="toggleDropdown"
    >
      <template #icon>
        <fluent-icon icon="checkmark-circle" size="16" />
      </template>
      <template v-if="isLoadingPipelines">
        <span class="max-w-32 truncate sm:max-w-48">{{
          $t('PIPELINE.LOADING_PIPELINES')
        }}</span>
      </template>
      <template v-else>
        <span class="max-w-32 truncate sm:max-w-48">{{
          visibleSelectedPipeline
            ? visibleSelectedPipeline.name
            : $t('PIPELINE.SELECT_PIPELINE')
        }}</span>
      </template>
      <fluent-icon icon="chevron-down" size="16" class="ml-1 shrink-0" />
    </Button>

    <!-- Dropdown para desktop -->
    <!-- `@mousedown.prevent`: mesmo par que deixava "Excluir" inerte no menu do
         card — lista rolável (max-h-80 + overflow) dentro de um wrapper com
         `@focusout`. O mousedown tira o foco, o focusout dispara e o container
         rola de volta ao topo antes do mouseup, então o `click` sobe para o
         menu em vez do item. Aqui só aparece com pipelines suficientes para
         gerar barra de rolagem; não reproduzido no dev (4 pipelines), corrigido
         pela mecânica idêntica. Ver PipelineCard.vue. -->
    <div
      v-if="isDropdownOpen && !isMobile"
      :id="dropdownId"
      role="menu"
      :aria-label="t('PIPELINE.SELECT_PIPELINE')"
      class="absolute top-[calc(100%+4px)] left-0 z-[9995] min-w-[220px] max-w-[min(320px,calc(100vw-2rem))] max-h-80 overflow-y-auto bg-n-solid-2 border border-n-weak rounded-xl shadow-lg p-1.5"
      @mousedown.prevent
    >
      <button
        v-for="(pipeline, index) in visiblePipelines"
        :key="pipeline.id"
        :ref="element => (optionRefs[index] = element)"
        type="button"
        role="menuitemradio"
        :tabindex="activeOptionIndex === index ? 0 : -1"
        :aria-checked="visibleSelectedPipeline?.id === pipeline.id"
        class="flex min-h-11 w-full items-center px-3 py-2.5 rounded-lg text-left text-sm font-medium text-n-slate-11 transition-colors duration-150 hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-brand"
        :class="
          visibleSelectedPipeline?.id === pipeline.id &&
          'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400'
        "
        @click="selectPipeline(pipeline)"
      >
        <span class="truncate">{{ pipeline.name }}</span>
        <fluent-icon
          v-if="visibleSelectedPipeline?.id === pipeline.id"
          icon="checkmark"
          size="16"
          class="ml-auto shrink-0 text-n-brand"
        />
      </button>
    </div>

    <!-- Modal para mobile -->
    <Modal
      v-model:show="showModal"
      size="small"
      @close="() => (showModal = false)"
    >
      <div class="p-4">
        <h3 data-modal-title class="mb-3 text-lg font-medium text-n-slate-12">
          {{ $t('PIPELINE.SELECTOR.TITLE') }}
        </h3>
        <div class="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
          <button
            v-for="pipeline in visiblePipelines"
            :key="pipeline.id"
            type="button"
            :aria-current="
              visibleSelectedPipeline?.id === pipeline.id ? 'true' : undefined
            "
            class="flex min-h-11 w-full items-center rounded-lg p-3 text-left text-n-slate-11 transition-colors duration-150 hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-n-brand"
            :class="
              visibleSelectedPipeline?.id === pipeline.id &&
              'bg-woot-50 text-woot-600 dark:bg-woot-900/20 dark:text-woot-300'
            "
            @click="selectPipeline(pipeline)"
          >
            <fluent-icon icon="checkmark-circle" size="18" class="mr-2" />
            <div class="font-medium break-words">
              {{ pipeline.name }}
            </div>
            <fluent-icon
              v-if="visibleSelectedPipeline?.id === pipeline.id"
              icon="checkmark"
              size="16"
              class="ml-auto text-woot-500"
            />
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>
